<template>
  <div
    :class="[
      'drag-drop-container border-4 border-gray-300 border-dashed bg-gray-100 rounded-xl pb-2 text-center relative',
      `size-${size}`
    ]"
    @dragover.prevent="handleDragOver"
    @drop.prevent="handleDrop"
  >
    <div v-if="file">
      <button
        class="absolute top-2 right-2 bg-gray-100 text-gray-950 rounded-full w-6 h-6 flex items-center justify-center hover:bg-gray-400 focus:outline-none"
        @click="clearFile"
      >
        <icon
          icon="x"
          color="black"
          size="16px"
        />
      </button>
      <div
        v-if="fileUrl && fileType === 'image'"
        class="p-3"
      >
        <img
          :src="fileUrl"
          alt="Image to upload"
        >
        <p v-if="!compact">
          Selected file: {{ file.name }}
        </p>
      </div>
      <div
        v-else-if="fileUrl && fileType === 'excel'"
        class="flex flex-row m-2 content-center pt-2 "
      >
        <icon
          icon="file_generic"
          color="#0369a1"
        />
        <p class="ml-2 ">
          {{ file.name }}
        </p>
      </div>
      <!--     Added PDF -->
      <div
        v-else-if="file && fileType === 'pdf'"
        class="flex flex-row m-2 content-center pt-2 "
      >
        <icon
          icon="file_generic"
          color="#dc2626"
        />
        <p class="ml-2 ">
          {{ file.name }}
        </p>
      </div>
    </div>

    <!-- Upload Box -->
    <div v-if="!file || multipleFiles">
      <input
        ref="fileInput"
        type="file"
        style="display: none;"
        :accept="accept"
        :multiple="multipleFiles"
        @change="handleFileSelect"
      >
      <div
        class="drop-zone"
        :class="size === 'small' ? 'compact-zone' : ''"
        @click="triggerFileInput"
      >
        <icon
          v-if="!compact"
          icon="upload_cloud"
          :size="size === 'small' ? '40px' : '80px'"
          color="#3a4473"
        />
        <div>
          <p>Drag & Drop your file here</p>
          <p class="font-bold text-sm m-4">
            OR
          </p>
          <div class="text-sm rounded-xl button-filled py-1 px-3 inline-block">
            <span>SELECT FILE</span>
            <span v-if="multipleFiles">S</span>
          </div>
        </div>
        <div v-if="errors.length > 0">
          <div
            v-for="error in errors"
            :key="error"
            class="text-xs text-default_error"
          >
            **{{ error }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, defineComponent, watch } from 'vue';
import Icon from "@/javascript/views/components/Icon.vue";

export default defineComponent({
  name: 'DragNDrop',
  components: {Icon},
  props: {
    size: {
      type: String,
      default: "default",
      validator: (value) => [
        'small',
        'default'
      ].includes(value)
    },
    validate: {
      type: Boolean,
      default: false,
    },
    fileType: {
      type: String,
      default: "image",
      validator: (value) => [
        'image',
        'excel',
        'pdf'
      ].includes(value)
    },
    accept: {
      type: String,
      default: null
    },
    compact: {
      type: Boolean,
      default: false
    },
    multipleFiles: {
      type: Boolean,
      default: false
    },
    maxFileSize: {
      type: Number,
      default: null
    }
  },
  emits: ['file-selected'],
  setup(props, { emit }) {
    const file = ref(null);
    const files = ref([]);
    const fileInput = ref(null);
    const fileUrl = ref(null);
    const errors = ref([]);

    const handleDragOver = (event) => {
      event.preventDefault(); // Prevent default behavior (Prevent file from being opened)
      event.dataTransfer.dropEffect = 'copy'; // Show a copy cursor
    };

    const isAcceptedFile = (f) => {
      if (!props.accept) return true;
      const accepted = props.accept.split(',').map(s => s.trim().toLowerCase());
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return accepted.includes(ext) || accepted.includes(f.type);
    };

    const handleDrop = (event) => {
      if (props.multipleFiles) {
        const droppedFiles = event.dataTransfer.files;
        for (let i = 0; i < droppedFiles.length; i++) {
          let f = droppedFiles[i];
          if (!isAcceptedFile(f)) {
            errors.value = [`Invalid file type. Accepted: ${props.accept}`];
            continue;
          }
          validateFile(f).then((res) => {
            if(res) {
              files.value.push(f);
            }
          });
        }
        emit('file-selected', files.value);

      } else {
        const droppedFile = event.dataTransfer.files[0];
        if (droppedFile) {
          if (!isAcceptedFile(droppedFile)) {
            errors.value = [`Invalid file type. Accepted: ${props.accept}`];
            return;
          }

          validateFile(droppedFile).then((res) => {
            if(res) {
              file.value = droppedFile;
              emit('file-selected', droppedFile); // Emit the file to the parent component
            }

          });
        }
      }
    };

    const handleFileSelect = async (event) => {
      console.log('file selected');
      if (props.multipleFiles) {
        const droppedFiles = event.target.files;
        for (let i = 0; i < droppedFiles.length; i++) {
          let f = droppedFiles[i];
          await validateFile(f).then((res) => {
            if (res) {
              files.value.push(f);
            }
          });
        }
        emit('file-selected', files.value);

      } else {
        const selectedFile = event.target.files[0];

        await validateFile(selectedFile).then((res) => {
          if (res) {
            file.value = selectedFile;
            emit('file-selected', selectedFile); // Emit the file to the parent component
          }
        });
      }
    };

    const validateFile = async (localFile) => {
      console.log('validating file', localFile);
      if(!props.validate && !props.maxFileSize) return true;
      errors.value = [];
      if (!localFile) return true;
      if (!(localFile instanceof File)) {
        errors.value.push('invalid file');
      }

      // File size validation
      if (props.maxFileSize && localFile.size > props.maxFileSize) {
        const maxMB = (props.maxFileSize / (1024 * 1024)).toFixed(0);
        errors.value.push(`File exceeds maximum size of ${maxMB}MB.`);
      }

      if (!props.validate) return errors.value.length === 0;

      const validTypes = ['jpeg', 'jpg', 'png', 'svg', 'webp'];
      if (!validTypes.some(type => localFile.type.includes(type))) {
        errors.value.push('invalid type');
      }

      // Removing File size restrictions for now
      // const maxSizeInBytes = 1024 * 1024; // 1MB in bytes
      // if (localFile.size > maxSizeInBytes) {
      //   errors.value.push('Image exceeds maximum size. Maximum dimensions are 1024 x 1024 pixels.');
      // }
      await checkImageDimensions(localFile).then((res) => {
        if (!res.isValid) {
          errors.value.push(res.message);
        }
      });
      return errors.value.length === 0;
    };


    // Promise to validate image dimensions
    function checkImageDimensions(file) {
      return new Promise((resolve) => {
        if (!file || !(file instanceof File)) {
          return resolve({ isValid: false, message: 'Invalid file.' });
        }

        const img = new Image();
        const objectURL = URL.createObjectURL(file);
        img.src = objectURL;

        // Handle image load
        img.onload = () => {
          const isValid = img.width >= 32 && img.height >= 32;
          resolve({
            isValid,
            message: isValid ? 'File is valid.' : 'Image must be at least 64x64 pixels.'
          });

          // Clean up the URL object
          URL.revokeObjectURL(objectURL);
        };

        // Handle image errors
        img.onerror = () => {
          resolve({ isValid: false, message: 'Failed to load image.' });

          // Clean up the URL object
          URL.revokeObjectURL(objectURL);
        };
      });
    }

    const triggerFileInput = () => {
      fileInput.value.click(); // Trigger the hidden file input
    };

    const clearFile = () => {
      file.value = null;
    };

    const removeFile = (index) => {
      files.value.splice(index, 1);
      emit('file-selected', files.value);
    };

    watch(file, (newFile) => {
      if(newFile){
        fileUrl.value = URL.createObjectURL(newFile);
      }else{
        fileUrl.value = "";
      }
      emit('file-selected', newFile); // Emit the file whenever it changes

    });

    return {
      file,
      files,
      fileInput,
      handleDragOver,
      handleDrop,
      handleFileSelect,
      triggerFileInput,
      clearFile,
      removeFile,
      errors,
      fileUrl
    };
  }
});
</script>

<style scoped lang="scss">

.drag-drop-container {
  &.size-small {
    @apply p-2 max-w-xs;
  }

  &.size-default {
    @apply p-4 max-w-full;
  }
}

.drop-zone {
  cursor: pointer;
  padding: 16px;
}

.drop-zone.compact-zone {
  padding: 8px;
}

.drop-zone p {
  margin: 0;
}
</style>
